import {
  ActionIcon,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import classes from "../../UserProfile.module.css";

import { TextSerif } from "@/components/elements";
import { Modal } from "@/components/elements/Modal";
import { IconEdit, IconMapPin, IconTrash } from "@/components/icons";
import { useAddAddress } from "@/services/profile/add-address";
import { useProfile } from "@/services/profile/get-profile";
import { useRemoveAddress } from "@/services/profile/remove-address";
import { useUpdateAddress } from "@/services/profile/update-address";

import type { AddAddressInput } from "@/services/profile/add-address";
import type { UserAddress } from "@/types/user";
import type { FC } from "react";

export const UserProfileTabAddress: FC = () => {
  const { t } = useTranslation();

  const [opened, { close, open }] = useDisclosure();
  const [confirmOpen, { open: openConfirm, close: closeConfirm }] = useDisclosure();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isChanged, setIsChanged] = useState(false);
  const [initialValues, setInitialValues] = useState<AddAddressInput>({
    address: "",
    country: "",
    postal_code: "",
    city: "",
  });

  const [addressToDeleteIndex, setAddressToDeleteIndex] = useState<number | null>(null);

  const { data: user } = useProfile();
  const addAddressMutation = useAddAddress();
  const updateAddressMutation = useUpdateAddress();
  const removeAddressMutation = useRemoveAddress();

  const addressList: UserAddress[] = user?.additional_info?.addresses || [];
  const min2CharactersErrorText = t("profile.validation.min_characters", { length: 2 });

  const form = useForm<AddAddressInput>({
    initialValues: {
      address: "",
      country: "",
      postal_code: "",
      city: "",
    },
    validate: {
      country: (value) => (value!.trim().length < 2 ? min2CharactersErrorText : null),
      address: (value) => (value!.trim().length < 2 ? min2CharactersErrorText : null),
      postal_code: (value) => (value!.trim().length < 2 ? min2CharactersErrorText : null),
      city: (value) => (value!.trim().length < 2 ? min2CharactersErrorText : null),
    },
  });

  const handleSubmit = (values: AddAddressInput) => {
    if (editingIndex !== null) {
      updateAddressMutation.mutate({ ...values, index: editingIndex }, { onSuccess: handleClose });
    } else {
      addAddressMutation.mutate(values, { onSuccess: handleClose });
    }
  };

  const handleClose = () => {
    form.reset();
    setEditingIndex(null);
    setIsChanged(false);
    close();
  };

  const handleOpenAdd = () => {
    const emptyValues = { address: "", country: "", postal_code: "", city: "" };
    form.setValues(emptyValues);
    setInitialValues(emptyValues);
    setEditingIndex(null);
    setIsChanged(false);
    open();
  };

  const handleEdit = (address: UserAddress, index: number) => {
    form.setValues(address);
    setInitialValues(address);
    setEditingIndex(index);
    setIsChanged(false);
    open();
  };

  const handleAskDelete = (index: number) => {
    setAddressToDeleteIndex(index);
    openConfirm();
  };

  const handleConfirmDelete = () => {
    if (addressToDeleteIndex !== null) {
      removeAddressMutation.mutate(addressToDeleteIndex, {
        onSuccess: () => {
          setAddressToDeleteIndex(null);
          closeConfirm();
        },
      });
    }
  };

  const isFormValid = () => {
    const hasErrors = Object.keys(form.errors).length > 0;
    return isChanged && !hasErrors;
  };

  return (
    <>
      <Box>
        {addressList.length > 0 && (
          <Group justify="end" align="center" h="2.5em" mb="lg">
            <Button onClick={handleOpenAdd} aria-label={t("profile.add_address")}>
              {t("profile.add_address")}
            </Button>
          </Group>
        )}

        <Flex
          direction="column"
          h="calc(100% - 2.5em)"
          mt={addressList.length > 0 ? "lg" : "0"}
          gap="lg"
        >
          {addressList.length > 0 ? (
            addressList.map((item, index) => (
              <Card key={`address_KEY_${index}`} p="lg">
                <Group justify="space-between" align="center" h="2.5em" p="md">
                  <Title order={3} fw="normal" fz="1.25em">
                    {t("profile.address")} {index + 1}
                  </Title>
                  <Flex gap="md" justify="end">
                    <ActionIcon
                      color="danger"
                      variant="light"
                      onClick={() => handleAskDelete(index)}
                    >
                      <IconTrash />
                    </ActionIcon>
                    <ActionIcon variant="light" onClick={() => handleEdit(item, index)}>
                      <IconEdit />
                    </ActionIcon>
                  </Flex>
                </Group>
                <Flex direction="column" gap="sm" mt="lg" p={"md"}>
                  <Group justify="space-between" align="center" w="100%">
                    <TextSerif size="sm">{t("profile.country")}</TextSerif>
                    <Text size="md">{item.country || "-"}</Text>
                  </Group>
                  <Box mt="xs" className={classes.userData_list} />
                  <Group justify="space-between" align="center" w="100%">
                    <TextSerif size="sm">{t("profile.address")}</TextSerif>
                    <Text size="md">{item.address || "-"}</Text>
                  </Group>
                  <Box mt="xs" className={classes.userData_list} />
                  <Group justify="space-between" align="center" w="100%">
                    <TextSerif size="sm">{t("profile.postal_code")}</TextSerif>
                    <Text size="md">{item.postal_code || "-"}</Text>
                  </Group>
                  <Box mt="xs" className={classes.userData_list} />
                  <Group justify="space-between" align="center" w="100%">
                    <TextSerif size="sm">{t("profile.city")}</TextSerif>
                    <Text size="md">{item.city || "-"}</Text>
                  </Group>
                  <Box mt="xs" className={classes.userData_list} />
                </Flex>
              </Card>
            ))
          ) : (
            <Card radius="md" h="24em">
              <Flex direction="column" justify="center" align="center" gap="md" h="100%">
                <TextSerif>{t("profile.no_address_data")}</TextSerif>
                <Button
                  variant="filled"
                  size="md"
                  onClick={handleOpenAdd}
                  aria-label={t("profile.add_address")}
                >
                  {t("profile.add_address")}
                </Button>
              </Flex>
            </Card>
          )}
        </Flex>
      </Box>

      {/* Modal Add/Edit */}
      <Modal
        opened={opened}
        onClose={handleClose}
        size="md"
        centered
        title={
          <Flex align="center" gap="md">
            <ThemeIcon radius="100%" color="primary" variant="light">
              <IconMapPin />
            </ThemeIcon>
            {editingIndex !== null ? t("profile.edit_address") : t("profile.add_address")}
          </Flex>
        }
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="xl">
            <Stack gap="sm">
              {["country", "address", "postal_code", "city"].map((field) => (
                <TextInput
                  key={field}
                  label={t(`profile.${field}`)}
                  {...form.getInputProps(field as keyof AddAddressInput)}
                  onChange={(e) => {
                    const val = e.currentTarget.value;
                    form.getInputProps(field as keyof AddAddressInput).onChange(e);

                    const updated = { ...form.values, [field]: val };
                    const hasChanges = Object.keys(updated).some((key) => {
                      const k = key as keyof AddAddressInput;
                      return updated[k] !== initialValues[k];
                    });

                    setIsChanged(hasChanges);
                  }}
                />
              ))}
            </Stack>

            <Group>
              <Button
                variant="outline"
                onClick={handleClose}
                flex={1}
                aria-label={t("common.cancel")}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                flex={1}
                disabled={!isFormValid()}
                aria-label={t("common.save")}
              >
                {t("common.save")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Modal Delete */}
      <Modal
        opened={confirmOpen}
        onClose={closeConfirm}
        size="sm"
        centered
        title={t("profile.delete_address")}
        icon={IconMapPin}
      >
        <Stack gap="lg">
          <TextSerif>{t("profile.confirm_delete")}</TextSerif>
          <Group>
            <Button
              variant="outline"
              onClick={closeConfirm}
              flex={1}
              aria-label={t("common.cancel")}
            >
              {t("common.cancel")}
            </Button>
            <Button
              color="danger"
              onClick={handleConfirmDelete}
              flex={1}
              aria-label={t("common.delete")}
            >
              {t("common.delete")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
